import { RefreshCcw, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { SamlEmailDiagnostic } from "@/components/SamlEmailDiagnostic";
import { TokenSection } from "@/components/TokenSection";
import { JsonViewer } from "@/components/JsonViewer";
import { AppShell } from "@/layout/AppShell";
import { decodeJwt } from "@/utils/jwt";
import { formatEpoch, relativeFromNow } from "@/utils/time";

/**
 * Deep session / token inspector — MyApps-style.
 *
 * Shows:
 *  * The ID token (always a JWT).
 *  * A Microsoft Graph access token (may be JWT, may be opaque).
 *  * MSAL account metadata (refresh-token presence is inferred – SPAs
 *    never see the RT itself by design).
 *  * Buttons to force a silent refresh and to re-sign-in.
 */
export function TokensPage() {
  return (
    <AppShell>
      <TokensContent />
    </AppShell>
  );
}

type GraphTokenState = {
  accessToken: string | null;
  scopes: string[];
  expiresOn: Date | null;
  error: string | null;
  loading: boolean;
};

function TokensContent() {
  const { account, claims, instance, acquireGraphToken, signIn } = useAuth();
  const idToken = account?.idToken ?? null;
  const decodedId = decodeJwt(idToken);
  const [graph, setGraph] = useState<GraphTokenState>({
    accessToken: null,
    scopes: [],
    expiresOn: null,
    error: null,
    loading: true,
  });

  const refresh = useCallback(
    async (forceRefresh = false) => {
      setGraph((g) => ({ ...g, loading: true, error: null }));
      try {
        const res = await acquireGraphToken(forceRefresh);
        setGraph({
          accessToken: res?.accessToken ?? null,
          scopes: res?.scopes ?? [],
          expiresOn: res?.expiresOn ?? null,
          error: res?.accessToken ? null : "No Graph access token available.",
          loading: false,
        });
      } catch (err) {
        setGraph({
          accessToken: null,
          scopes: [],
          expiresOn: null,
          error: err instanceof Error ? err.message : "Token request failed",
          loading: false,
        });
      }
    },
    [acquireGraphToken],
  );

  useEffect(() => {
    void refresh(false);
  }, [refresh]);

  const decodedGraph = decodeJwt(graph.accessToken);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/myapps"
            className="text-xs font-medium text-brand-700 hover:underline"
          >
            ← Back to My Apps
          </Link>
          <h1 className="mt-2 flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <ShieldCheck className="h-6 w-6 text-brand-600" />
            Session &amp; tokens
          </h1>
          <p className="mt-1 max-w-xl text-sm text-slate-600">
            Inspect the exact tokens Microsoft Entra handed you. Use this page
            when debugging sign-ins, claim mapping, or API calls.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refresh(true)}
            disabled={graph.loading}
            className="btn-secondary text-xs"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            {graph.loading ? "Refreshing…" : "Get new token"}
          </button>
          <button
            type="button"
            onClick={() => signIn(window.location.pathname)}
            className="btn-ghost text-xs"
          >
            Re-authenticate
          </button>
        </div>
      </header>

      <SessionSummary />

      <div className="flex flex-col gap-3">
        <TokenSection
          title="ID token"
          subtitle={
            decodedId?.payload?.iss
              ? String(decodedId.payload.iss)
              : "Identifies the signed-in user to the SPA."
          }
          raw={idToken}
          decoded={decodedId}
          defaultOpen
        />

        <TokenSection
          title="Microsoft Graph access token"
          subtitle={
            graph.error
              ? `Error: ${graph.error}`
              : graph.scopes.length
                ? `Scopes: ${graph.scopes.join(" ")}`
                : "Bearer token used for graph.microsoft.com calls."
          }
          raw={graph.accessToken}
          decoded={decodedGraph}
          extraRows={[
            {
              label: "Acquired scopes",
              value: graph.scopes.length ? (
                <div className="flex flex-wrap gap-1">
                  {graph.scopes.map((s) => (
                    <span key={s} className="chip">
                      {s}
                    </span>
                  ))}
                </div>
              ) : (
                "—"
              ),
            },
            {
              label: "Expires (MSAL)",
              value: graph.expiresOn
                ? `${graph.expiresOn.toLocaleString()} (${relativeFromNow(
                    Math.floor(graph.expiresOn.getTime() / 1000),
                  )})`
                : "—",
            },
          ]}
        />

        <TokenSection
          title="Refresh token"
          subtitle="Single-page applications never see the refresh token directly – Microsoft Entra keeps it in a same-site httpOnly cookie. We surface the related session metadata instead."
          raw="spa-cookie-bound"
          decoded={null}
          extraRows={[
            {
              label: "Home account id",
              value: account?.homeAccountId ?? "—",
            },
            {
              label: "Local account id",
              value: account?.localAccountId ?? "—",
            },
            {
              label: "Environment",
              value: account?.environment ?? "—",
            },
            {
              label: "Active in MSAL cache",
              value: instance.getActiveAccount() ? "Yes" : "No",
            },
          ]}
        />

        <TokenSection
          title="Account + claims"
          subtitle="Snapshot of the AccountInfo MSAL maintains locally."
          raw="account-snapshot"
          decoded={null}
          defaultOpen
          extraRows={[
            {
              label: "Tenant id (tid)",
              value: (claims?.tid as string | undefined) ?? "—",
            },
            {
              label: "Name",
              value: account?.name ?? "—",
            },
            {
              label: "Username (UPN)",
              value: account?.username ?? "—",
            },
          ]}
        />

        <div className="card p-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Raw MSAL account
          </h4>
          <JsonViewer className="mt-2" data={account ?? {}} />
        </div>
      </div>

      {/*
        AADSTS5000819 diagnostic — rendered below all existing token
        sections so operators can scroll past the happy-path data and
        land on the SAML email / domain check when a sign-in fails.
      */}
      <SamlEmailDiagnostic />
    </div>
  );
}

function SessionSummary() {
  const { account, claims } = useAuth();
  const exp = typeof claims?.exp === "number" ? claims.exp : null;
  const iat = typeof claims?.iat === "number" ? claims.iat : null;

  return (
    <div className="card grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
      <SummaryCell
        label="Signed in as"
        value={account?.username ?? (claims?.preferred_username as string | undefined) ?? "—"}
      />
      <SummaryCell
        label="Token issued"
        value={iat ? `${formatEpoch(iat)} (${relativeFromNow(iat)})` : "—"}
      />
      <SummaryCell
        label="ID token expires"
        value={exp ? `${formatEpoch(exp)} (${relativeFromNow(exp)})` : "—"}
      />
    </div>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 truncate text-sm text-slate-800">{value}</div>
    </div>
  );
}
